import {
  ThemeEvents,
  CartUpdateEvent,
  setCartDrawerLoading,
} from '@theme/events';
import {
  applyCartResponse,
  getCartSectionsParam,
  isCartSyncEnabled,
  refreshCartPage,
  runCartSyncRefreshes,
} from '@theme/cart-drawer';
import Splide from '@theme/splide';
import { debounce } from '@theme/utilities';

class CartPage extends HTMLElement {
  #controller = null;
  #backgroundSyncController = null;
  #isUpdating = false;
  #bonusesSplide = null;
  #bonusesSlides = [];
  #bonusesMediaQuery = window.matchMedia('(max-width: 1199px)');
  #handleBonusesMediaChange = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#handleBonusesMediaChange = debounce(this.#onBonusesMediaChange.bind(this), 150);
    this.#bonusesMediaQuery.addEventListener('change', this.#handleBonusesMediaChange);

    this.addEventListener('click', this.#onClick.bind(this), { signal });
    this.addEventListener('submit', this.#onSubmit.bind(this), { signal });
    this.addEventListener('toggle', this.#onBonusesToggle.bind(this), { signal, capture: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate.bind(this), { signal });

    requestAnimationFrame(() => {
      this.#lockBonusesAccordion();
      this.#syncBonusesSlider();
    });
  }

  disconnectedCallback() {
    this.#bonusesMediaQuery.removeEventListener('change', this.#handleBonusesMediaChange);
    this.#handleBonusesMediaChange?.cancel?.();
    this.#destroyBonusesSlider();
    this.#backgroundSyncController?.abort();
    this.#controller?.abort();
  }

  refresh() {
    return refreshCartPage(this.dataset.sectionId);
  }

  #onBonusesMediaChange() {
    this.#lockBonusesAccordion();
    this.#syncBonusesSlider();
  }

  /** Keep bonuses expanded on mobile; accordion only works on desktop. */
  #lockBonusesAccordion() {
    const details = this.querySelector('.cart-page-bonuses__details');

    if (!(details instanceof HTMLDetailsElement)) return;

    if (this.#bonusesMediaQuery.matches) {
      details.open = true;
    }
  }

  #syncBonusesSlider() {
    const bonusCount = Number(this.querySelector('[data-cart-bonuses]')?.dataset.bonusCount) || 0;

    if (this.#bonusesMediaQuery.matches && bonusCount > 1) {
      this.#initBonusesSlider();
      return;
    }

    this.#destroyBonusesSlider();
  }

  #resyncBonusesSlider() {
    this.#lockBonusesAccordion();
    this.#destroyBonusesSlider();
    this.#syncBonusesSlider();
  }

  /** @param {Event} event */
  #onBonusesToggle(event) {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('cart-page-bonuses__details')) {
      return;
    }

    if (this.#bonusesMediaQuery.matches) {
      details.open = true;
      return;
    }

    if (details.open) {
      requestAnimationFrame(() => this.#resyncBonusesSlider());
    }
  }

  #initBonusesSlider() {
    if (this.#bonusesSplide) return;

    const root = this.querySelector('[data-cart-bonuses-slider]');
    const list = root?.querySelector('.cart-page-bonuses__list');
    const details = root?.closest('details');

    if (!root || !list) return;
    if (details instanceof HTMLDetailsElement && !details.open) return;

    this.#bonusesSlides = Array.from(list.children);
    if (this.#bonusesSlides.length < 2) return;

    this.#bonusesSlides.forEach((slide) => {
      slide.classList.add('splide__slide');
    });

    // Apply horizontal list styles before mount so Splide measures correct widths.
    this.querySelector('[data-cart-bonuses]')?.classList.add('cart-page-bonuses--slider-ready');

    this.#bonusesSplide = new Splide(root, {
      type: 'slide',
      perPage: 1,
      gap: '8px',
      padding: { right: '48px' },
      arrows: false,
      pagination: false,
      drag: true,
      speed: 400,
      updateOnMove: true,
    });

    this.#bonusesSplide.on('mounted move updated', () => this.#syncBonusesArrowState());
    this.#bonusesSplide.mount();
    this.#syncBonusesArrowState();

    requestAnimationFrame(() => {
      this.#bonusesSplide?.refresh();
      this.#syncBonusesArrowState();
    });
  }

  #syncBonusesArrowState() {
    if (!this.#bonusesSplide) return;

    const prev = this.querySelector('[data-cart-bonuses-prev]');
    const next = this.querySelector('[data-cart-bonuses-next]');
    const index = this.#bonusesSplide.index;
    const end = this.#bonusesSplide.Components.Controller.getEnd();

    if (prev instanceof HTMLButtonElement) {
      prev.disabled = index <= 0;
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = index >= end;
    }
  }

  #destroyBonusesSlider() {
    if (this.#bonusesSplide) {
      try {
        this.#bonusesSplide.destroy(true);
      } catch {
        // Root may already be detached after a section re-render.
      }
      this.#bonusesSplide = null;
    }

    this.#bonusesSlides.forEach((slide) => {
      if (slide.isConnected) {
        slide.classList.remove('splide__slide');
      }
    });
    this.#bonusesSlides = [];

    this.querySelector('[data-cart-bonuses]')?.classList.remove('cart-page-bonuses--slider-ready');

    const prev = this.querySelector('[data-cart-bonuses-prev]');
    const next = this.querySelector('[data-cart-bonuses-next]');

    if (prev instanceof HTMLButtonElement) prev.disabled = true;
    if (next instanceof HTMLButtonElement) next.disabled = false;
  }

  /** @param {CustomEvent} event */
  async #onCartUpdate(event) {
    const source = event.detail?.data?.source;

    if (source === 'cart-page') {
      return;
    }

    try {
      await this.refresh();
      this.#resyncBonusesSlider();
    } catch (error) {
      console.error('[cart-page] Failed to refresh after cart update', error);
    }
  }

  /** @param {Event} event */
  async #onSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.matches('[data-cart-discount-form]')) {
      return;
    }

    event.preventDefault();
    await this.#applyDiscount(form);
  }

  /** @param {Event} event */
  async #onClick(event) {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const prevBonus = target.closest('[data-cart-bonuses-prev]');
    if (prevBonus instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#bonusesSplide?.go('<');
      return;
    }

    const nextBonus = target.closest('[data-cart-bonuses-next]');
    if (nextBonus instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#bonusesSplide?.go('>');
      return;
    }

    const summary = target.closest('.cart-page-bonuses__summary');
    if (summary && this.#bonusesMediaQuery.matches) {
      event.preventDefault();
      return;
    }

    const decreaseButton = target.closest('[data-cart-quantity-decrease]');
    if (decreaseButton instanceof HTMLButtonElement) {
      event.preventDefault();
      await this.#changeQuantity(decreaseButton, -1);
      return;
    }

    const increaseButton = target.closest('[data-cart-quantity-increase]');
    if (increaseButton instanceof HTMLButtonElement) {
      event.preventDefault();
      await this.#changeQuantity(increaseButton, 1);
      return;
    }

    const removeButton = target.closest('[data-cart-remove]');
    if (removeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      await this.#changeLineQuantity(removeButton.dataset.lineKey, 0);
      return;
    }

    const removeDiscountBtn = target.closest('[data-cart-discount-remove]');
    if (removeDiscountBtn) {
      event.preventDefault();
      await this.#removeDiscount();
      return;
    }

    const offerButton = target.closest('[data-cart-special-offer-add]');
    if (offerButton instanceof HTMLButtonElement) {
      event.preventDefault();
      await this.#addSpecialOffer(offerButton);
    }
  }

  async #removeDiscount() {
    if (this.#isUpdating) return;
    this.#isUpdating = true;
    setCartDrawerLoading({ loading: true });
    this.classList.add('cart-page--loading');

    try {
      const sections = getCartSectionsParam();
      const response = await fetch(`${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: '',
          ...(sections ? { sections } : {}),
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error('Failed to remove discount');
      }

      await applyCartResponse(result);
      this.#resyncBonusesSlider();

      document.dispatchEvent(
        new CartUpdateEvent(result, 'cart-page', {
          source: 'cart-page',
        }),
      );
    } catch (error) {
      console.error('[cart-page] Failed to remove discount', error);
    } finally {
      this.#isUpdating = false;
      this.classList.remove('cart-page--loading');
      setCartDrawerLoading({ loading: false });
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {number} delta
   */
  async #changeQuantity(button, delta) {
    const lineKey = button.dataset.lineKey;
    const quantityElement = button.closest('[data-cart-quantity]')?.querySelector('[data-cart-quantity-value]');
    const currentQuantity = Number(quantityElement?.textContent) || 1;
    const maxQuantity = button.dataset.maxQuantity ? Number(button.dataset.maxQuantity) : null;
    let nextQuantity = currentQuantity + delta;

    if (nextQuantity < 1) {
      nextQuantity = 1;
    }

    if (maxQuantity != null && nextQuantity > maxQuantity) {
      nextQuantity = maxQuantity;
    }

    if (nextQuantity === currentQuantity) return;

    await this.#changeLineQuantity(lineKey, nextQuantity);
  }

  /**
   * @param {string | undefined} lineKey
   * @param {number} quantity
   */
  async #changeLineQuantity(lineKey, quantity) {
    if (!lineKey || this.#isUpdating) return;

    this.#isUpdating = true;
    setCartDrawerLoading({ loading: true });
    this.classList.add('cart-page--loading');

    try {
      const sections = getCartSectionsParam();
      const response = await fetch(`${window.Theme?.routes?.cart_change_url || '/cart/change'}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity,
          ...(sections ? { sections } : {}),
        }),
      });

      if (!response.ok) {
        throw new Error('Cart update failed');
      }

      const cart = await response.json();
      await applyCartResponse(cart);
      this.#resyncBonusesSlider();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-page', {
          source: 'cart-page',
        }),
      );

      if (quantity === 0 && isCartSyncEnabled()) {
        this.#monitorAppSync();
      }
    } catch (error) {
      console.error('[cart-page] Failed to update cart line', error);
    } finally {
      this.#isUpdating = false;
      this.classList.remove('cart-page--loading');
      setCartDrawerLoading({ loading: false });
    }
  }

  async #monitorAppSync() {
    this.#backgroundSyncController?.abort();
    this.#backgroundSyncController = new AbortController();
    const { signal } = this.#backgroundSyncController;

    try {
      await runCartSyncRefreshes(async () => {
        await this.refresh();
        this.#resyncBonusesSlider();
      }, signal);
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      console.error('[cart-page] Cart sync failed', error);
    }
  }

  /** @param {HTMLButtonElement} button */
  async #addSpecialOffer(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || this.#isUpdating) return;

    this.#isUpdating = true;
    setCartDrawerLoading({ loading: true });
    this.classList.add('cart-page--loading');
    button.disabled = true;

    try {
      const sections = getCartSectionsParam();
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
          ...(sections ? { sections } : {}),
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = result.description || result.message || 'Special offer add failed';
        throw new Error(message);
      }

      await applyCartResponse(result);
      this.#resyncBonusesSlider();

      document.dispatchEvent(
        new CartUpdateEvent(result, 'cart-page', {
          source: 'cart-page',
        }),
      );
    } catch (error) {
      console.error('[cart-page] Failed to add special offer', error);
      button.disabled = false;
    } finally {
      this.#isUpdating = false;
      this.classList.remove('cart-page--loading');
      setCartDrawerLoading({ loading: false });
    }
  }

  /** @param {HTMLFormElement} form */
  async #applyDiscount(form) {
    if (this.#isUpdating) return;

    const input = form.querySelector('[data-cart-discount-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const errorEl = form.querySelector('[data-cart-discount-error]');
    const enteredCode = input.value.trim();

    this.#clearDiscountError(form);

    if (!enteredCode) {
      const message =
        errorEl?.dataset?.errorRequired ||
        errorEl?.dataset?.errorInvalid ||
        'Enter a discount code';
      this.#showDiscountError(form, message);
      input.focus();
      return;
    }

    this.#isUpdating = true;
    setCartDrawerLoading({ loading: true });
    this.classList.add('cart-page--loading');

    try {
      const sections = getCartSectionsParam();
      const response = await fetch(`${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: enteredCode,
          ...(sections ? { sections } : {}),
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = result.description || result.message || errorEl?.dataset?.errorInvalid || 'Enter a valid discount code';
        this.#showDiscountError(form, message);
        return;
      }

      const discountCodes = Array.isArray(result.discount_codes) ? result.discount_codes : [];
      const isApplied = discountCodes.some(
        (discount) => discount.code && discount.code.toLowerCase() === enteredCode.toLowerCase() && discount.applicable === true,
      );

      if (!isApplied) {
        const message = errorEl?.dataset?.errorInvalid || 'Enter a valid discount code';
        this.#showDiscountError(form, message);
        return;
      }

      await applyCartResponse(result);
      this.#resyncBonusesSlider();

      document.dispatchEvent(
        new CartUpdateEvent(result, 'cart-page', {
          source: 'cart-page',
        }),
      );
    } catch (error) {
      console.error('[cart-page] Failed to apply discount', error);
      const message = error?.message || errorEl?.dataset?.errorInvalid || 'Enter a valid discount code';
      this.#showDiscountError(form, message);
    } finally {
      this.#isUpdating = false;
      this.classList.remove('cart-page--loading');
      setCartDrawerLoading({ loading: false });
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #showDiscountError(form, message) {
    form.classList.add('has-error');

    const input = form.querySelector('[data-cart-discount-input]');
    if (input instanceof HTMLInputElement) {
      input.setAttribute('aria-invalid', 'true');
      input.addEventListener('input', () => this.#clearDiscountError(form), { once: true });
    }

    const errorEl = form.querySelector('[data-cart-discount-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  /** @param {HTMLFormElement} form */
  #clearDiscountError(form) {
    form.classList.remove('has-error');

    const input = form.querySelector('[data-cart-discount-input]');
    if (input instanceof HTMLInputElement) {
      input.removeAttribute('aria-invalid');
    }

    const errorEl = form.querySelector('[data-cart-discount-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage);
}
